import { database } from './environment';
import { OAuthResponseError, TokenRefreshError } from './errors';
import { OAuthServerAgent } from './server-agent';
import type { Did, Session } from './types';
import { locks } from './utils';

export interface SessionGetOptions {
	signal?: AbortSignal;
	noCache?: boolean;
	allowStale?: boolean;
}

type PendingItem<V> = Promise<{ value: V; isFresh: boolean }>;
const pending = new Map<Did, Promise<PendingItem<Session>>>();

export const getSession = async (sub: Did, options?: SessionGetOptions): Promise<Session> => {
	options?.signal?.throwIfAborted();

	let allowStored = isTokenUsable;
	if (options?.noCache) {
		allowStored = returnFalse;
	} else if (options?.allowStale) {
		allowStored = returnTrue;
	}

	let previousExecutionFlow: Promise<PendingItem<Session>> | undefined;
	while ((previousExecutionFlow = pending.get(sub))) {
		try {
			const { isFresh, value } = await previousExecutionFlow;

			if (isFresh || allowStored(value)) {
				return value;
			}
		} catch {
			// Ignore errors from previous execution flows.
		}

		options?.signal?.throwIfAborted();
	}

	const run = async (): Promise<PendingItem<Session>> => {
		const storedSession = database.sessions.get(sub);

		if (storedSession && allowStored(storedSession)) {
			return { isFresh: false, value: storedSession };
		}

		const newSession = await refreshToken(sub, storedSession);

		await storeSession(sub, newSession);
		return { isFresh: true, value: newSession };
	};

	let promise: Promise<PendingItem<Session>>;

	if (locks) {
		promise = locks.request<PendingItem<Session>>(`atcute-oauth:${sub}`, run as any);
	} else {
		promise = run();
	}

	promise = promise.finally(() => pending.delete(sub));

	if (pending.has(sub)) {
		throw new Error('concurrent request for the same key');
	}

	pending.set(sub, promise);

	const { value } = await promise;
	return value;
};

export const storeSession = async (sub: Did, newSession: Session): Promise<void> => {
	try {
		database.sessions.set(sub, newSession);
	} catch (err) {
		await onRefreshError(newSession);
		throw err;
	}
};

export const deleteStoredSession = (sub: Did): void => {
	database.sessions.delete(sub);
};

export const getStoredSession = (sub: Did): Session | undefined => {
	return database.sessions.get(sub);
};

export const listStoredSessions = (): Did[] => {
	return database.sessions.keys() as Did[];
};

const returnTrue = () => true;
const returnFalse = () => false;

const refreshToken = async (sub: Did, storedSession: Session | undefined): Promise<Session> => {
	if (storedSession === undefined) {
		throw new TokenRefreshError(sub, `session deleted by another tab`);
	}

	const { dpopKey, info, token } = storedSession;
	const server = new OAuthServerAgent(info.server, dpopKey);

	try {
		const newToken = await server.refresh({ sub: info.sub, token });

		return { dpopKey, info, token: newToken };
	} catch (cause) {
		if (cause instanceof OAuthResponseError && cause.status === 400 && cause.error === 'invalid_grant') {
			throw new TokenRefreshError(sub, `session was revoked`, { cause });
		}

		throw cause;
	}
};

const onRefreshError = async ({ dpopKey, info, token }: Session) => {
	const server = new OAuthServerAgent(info.server, dpopKey);
	await server.revoke(token.refresh ?? token.access);
};

const isTokenUsable = ({ token }: Session): boolean => {
	const expires = token.expires_at;
	return expires == null || Date.now() + 60_000 <= expires;
};
